import {
  FiArchive,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiGrid,
  FiList,
  FiSettings,
  FiSidebar,
} from "react-icons/fi";
import { boardPublicPath } from "@/config/browserBoard";
import { ArchiveSearchBar } from "@/features/archive/components/ArchiveSearchBar";
import type { SearchScope } from "@/features/search/searchScope";
import { DesktopRefreshLabel } from "@/shared/ui/navigation/DesktopRefreshLabel";

interface DesktopArchiveToolbarProps {
  isFetching: boolean;
  searchQuery?: string;
  searchScope?: SearchScope;
  onClearSearch?: () => void;
  onRefresh: () => void;
  onSearch?: (query: string) => void;
  onSearchScopeChange?: (scope: SearchScope) => void;
  onToggleSidebar: () => void;
  onScrollToTop: () => void;
  onScrollToBottom: () => void;
  onSettingsClick: React.MouseEventHandler<HTMLAnchorElement>;
}

/** カタログと同じ外観で、過去ログページに有効な操作を表示する。 */
export function DesktopArchiveToolbar({
  isFetching,
  searchQuery = "",
  searchScope = "all",
  onClearSearch = (): void => undefined,
  onRefresh,
  onSearch = (): void => undefined,
  onSearchScopeChange = (): void => undefined,
  onToggleSidebar,
  onScrollToTop,
  onScrollToBottom,
  onSettingsClick,
}: DesktopArchiveToolbarProps): React.ReactElement {
  return (
    <nav
      className="desktop-catalog-toolbar"
      aria-label="過去ログナビゲーション"
    >
      <fieldset
        className="desktop-catalog-toolbar-navigation"
        aria-label="ページ内移動"
      >
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={onToggleSidebar}
          aria-label="履歴サイドバーを切り替える"
        >
          <FiSidebar aria-hidden="true" />
        </button>
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={onScrollToTop}
          aria-label="ページ上部へ移動"
        >
          <FiChevronUp aria-hidden="true" />
        </button>
        <button
          className="desktop-catalog-toolbar-icon"
          type="button"
          onClick={onScrollToBottom}
          aria-label="ページ下部へ移動"
        >
          <FiChevronDown aria-hidden="true" />
        </button>
      </fieldset>

      <fieldset
        className="desktop-catalog-toolbar-primary"
        aria-label="カタログ表示"
      >
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/board")}
        >
          <FiList aria-hidden="true" />
          リスト
        </a>
        <a className="desktop-catalog-toolbar-link" href={boardPublicPath("/")}>
          <FiGrid aria-hidden="true" />
          カタログ
        </a>
      </fieldset>

      <fieldset
        className="desktop-catalog-toolbar-secondary"
        aria-label="過去ログ操作"
      >
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/history/viewed")}
        >
          <FiClock aria-hidden="true" />
          履歴
        </a>
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/archive")}
          aria-current="page"
        >
          <FiArchive aria-hidden="true" />
          過去ログ
        </a>
        <a
          className="desktop-catalog-toolbar-link"
          href={boardPublicPath("/settings")}
          onClick={onSettingsClick}
        >
          <FiSettings aria-hidden="true" />
          設定
        </a>
        <button
          className="desktop-catalog-toolbar-action desktop-catalog-toolbar-refresh"
          type="button"
          disabled={isFetching}
          onClick={onRefresh}
        >
          <DesktopRefreshLabel isFetching={isFetching} />
        </button>
      </fieldset>

      <ArchiveSearchBar
        className="desktop-catalog-toolbar-search"
        isFetching={isFetching}
        query={searchQuery}
        scope={searchScope}
        onClear={onClearSearch}
        onScopeChange={onSearchScopeChange}
        onSubmit={onSearch}
      />
    </nav>
  );
}
